import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { enUS } from '../locales/en-US';
import { ptBR } from '../locales/pt-BR';

// Define types for our translations and context
export type Translations = typeof enUS;
export type AvailableLanguages = 'en-US' | 'pt-BR';

interface LanguageContextType {
  language: AvailableLanguages;
  translations: Translations;
  toggleLanguage: () => void;
  getLanguageFlag: () => string;
}

interface LanguageProviderProps {
  children: ReactNode;
}

// Create the language context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en-US',
  translations: enUS,
  toggleLanguage: () => {},
  getLanguageFlag: () => '🇺🇸'
});

// Get translations based on language code
const getTranslations = (lang: AvailableLanguages): Translations => {
  switch (lang) {
    case 'pt-BR':
      return ptBR;
    case 'en-US':
    default:
      return enUS;
  }
};

// Default language based on browser or location with safe fallback
const getDefaultLanguage = (): AvailableLanguages => {
  try {
    // First check for stored preference
    if (typeof window !== 'undefined') {
      const storedLang = localStorage.getItem('preferredLanguage');
      if (storedLang === 'pt-BR' || storedLang === 'en-US') {
        return storedLang;
      }
      
      // Browser language detection
      const browserLang = navigator.language;
      if (browserLang && browserLang.startsWith('pt')) {
        return 'pt-BR';
      }
    }
  } catch (error) {
    console.error('Error detecting language preference:', error);
  }
  
  // Default to English
  return 'en-US';
};

// Language Provider Component
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<AvailableLanguages>(getDefaultLanguage());
  const [translations, setTranslations] = useState<Translations>(getTranslations(language));
  
  // Update translations when language changes
  useEffect(() => {
    setTranslations(getTranslations(language));

    // Keep the document language in sync for screen readers and search engines
    try {
      if (typeof document !== 'undefined') {
        document.documentElement.lang = language;
      }
    } catch (error) {
      console.error('Error setting document language:', error);
    }
    
    // Store preference safely
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('preferredLanguage', language);
      }
    } catch (error) {
      console.error('Error storing language preference:', error);
    }
  }, [language]);

  // Toggle between languages
  const toggleLanguage = (): void => {
    setLanguage(prev => prev === 'en-US' ? 'pt-BR' : 'en-US');
  };

  // Get the flag emoji for current language
  const getLanguageFlag = (): string => {
    return language === 'en-US' ? '🇺🇸' : '🇧🇷';
  };

  const contextValue: LanguageContextType = {
    language,
    translations,
    toggleLanguage,
    getLanguageFlag
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  // Context will never be undefined since we provided default values
  return context;
};

export default LanguageContext;